import { SpanStatusCode, trace } from '@opentelemetry/api';
import type {
  ClientInstrumentation,
  InstrumentationHandlerResult,
  ServerInstrumentation,
} from 'react-router';

const tracer = trace.getTracer('gets-events');

type OtelAttributes = Record<string, string | undefined>;

function filterAttributes(attributes: OtelAttributes) {
  return Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => value !== undefined),
  );
}

async function otelSpan(
  name: string,
  attributes: OtelAttributes,
  cb: () => Promise<InstrumentationHandlerResult>,
) {
  const filtered = filterAttributes(attributes);

  return tracer.startActiveSpan(
    name,
    { attributes: filtered },
    async (span) => {
      const { error } = await cb();

      if (error) {
        span.recordException(error);
        span.setStatus({ code: SpanStatusCode.ERROR });
      }

      span.end();
    },
  );
}

export const otelServerInstrumentation: ServerInstrumentation = {
  handler({ instrument }) {
    instrument({
      request: (callRequest, { request }) =>
        otelSpan(
          'request',
          { url: request.url, method: request.method },
          callRequest,
        ),
    });
  },
  route({ instrument, id }) {
    instrument({
      middleware: (fn, { pattern }) =>
        otelSpan('middleware', { routeId: id, pattern }, fn),
      loader: (fn, { pattern }) =>
        otelSpan('loader', { routeId: id, pattern }, fn),
      action: (fn, { pattern }) =>
        otelSpan('action', { routeId: id, pattern }, fn),
    });
  },
};

export const otelClientInstrumentation: ClientInstrumentation = {
  router({ instrument }) {
    instrument({
      navigate: (fn, { to, currentUrl }) =>
        otelSpan('navigation', { to: String(to), currentUrl }, fn),
      fetch: (fn, { href, fetcherKey, currentUrl }) =>
        otelSpan('fetcher', { href, fetcherKey, currentUrl }, fn),
    });
  },
  route({ instrument, id }) {
    instrument({
      middleware: (fn, { pattern }) =>
        otelSpan('middleware', { routeId: id, pattern }, fn),
      loader: (fn, { pattern }) =>
        otelSpan('loader', { routeId: id, pattern }, fn),
      action: (fn, { pattern }) =>
        otelSpan('action', { routeId: id, pattern }, fn),
    });
  },
};
